import Axios from '@/utils/axios';
import { getBackendErrorMessage } from '@/utils/errorHandler';

// GET /v1/vehicle-telemetry — the unit's GPS trail in a window. Vehicle-only
// mode returns ONE group regardless of handover, which is what a movement
// history needs. The API insists on ISO timestamps WITH timezone, so dates
// go over the wire as UTC `toISOString()`.
const BASE = '/v1/vehicle-telemetry';

export interface VehicleTrailPoint {
  /** ISO timestamp. */
  positioningTime: string;
  /** Same instant as epoch ms, parsed once here so no consumer re-parses. */
  positioningMs: number;
  latitude: number;
  longitude: number;
  speed: number;
  course: number;
  ignition: boolean | null;
  movement: boolean;
}

export interface VehicleTrail {
  vehicleID: string;
  platNomor: string | null;
  dateFrom: string;
  dateTo: string;
  /** Sorted by positioningTime ascending, invalid coordinates dropped. */
  points: VehicleTrailPoint[];
}

const isFinitePair = (lat: unknown, lng: unknown): boolean =>
  typeof lat === 'number' &&
  typeof lng === 'number' &&
  Number.isFinite(lat) &&
  Number.isFinite(lng) &&
  !(lat === 0 && lng === 0);

export const getVehicleTrail = async (
  vehicleID: string,
  dateFrom: Date,
  dateTo: Date
): Promise<VehicleTrail> => {
  try {
    const response = await Axios.get(BASE, {
      params: {
        vehicleId: vehicleID,
        dateFrom: dateFrom.toISOString(),
        dateTo: dateTo.toISOString(),
      },
    });
    const body = response.data?.data ?? response.data ?? {};
    const groups: any[] = Array.isArray(body.vehicles) ? body.vehicles : [];
    const points: VehicleTrailPoint[] = groups
      .flatMap((g) => (Array.isArray(g.points) ? g.points : []))
      .filter((p: any) => isFinitePair(p.latitude, p.longitude))
      .map((p: any) => ({
        positioningTime: p.positioningTime,
        positioningMs: new Date(p.positioningTime).getTime(),
        latitude: p.latitude,
        longitude: p.longitude,
        speed: Number.isFinite(Number(p.speed)) ? Number(p.speed) : 0,
        course: Number.isFinite(Number(p.course)) ? Number(p.course) : 0,
        ignition: p.ignition ?? null,
        movement: Boolean(p.movement),
      }))
      .sort(
        (a: VehicleTrailPoint, b: VehicleTrailPoint) =>
          a.positioningMs - b.positioningMs
      );
    return {
      vehicleID,
      platNomor: groups[0]?.platNomor ?? null,
      dateFrom: body.dateFrom ?? dateFrom.toISOString(),
      dateTo: body.dateTo ?? dateTo.toISOString(),
      points,
    };
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};
